import { useState, useEffect, useCallback, useRef } from "react";
import { X, Tag, Mail, Check } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "couponClaimed";
const SS_KEY = "couponPopupDismissed"; // session-level: cleared when browser/tab closes

const ALLOWED_PATHS = ["/", "/index.html", "/buy", "/buy.html"];

const CouponPopup = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [config, setConfig] = useState(null);
  const { lang } = useLanguage();

  // Refs to avoid stale closures in event listeners
  const visibleRef = useRef(false);
  const sentRef = useRef(false);
  useEffect(() => { visibleRef.current = visible; }, [visible]);
  useEffect(() => { sentRef.current = sent; }, [sent]);

  const isAdminPage = () => {
    const path = window.location.pathname;
    return path.startsWith("/dariousdmt") || path.startsWith("/admin");
  };

  const alreadyClaimed = () => localStorage.getItem(LS_KEY) === "true";
  const dismissedThisSession = () => sessionStorage.getItem(SS_KEY) === "true";

  const isAllowedPage = () => ALLOWED_PATHS.includes(window.location.pathname);

  // Fetch coupon config from backend
  useEffect(() => {
    if (isAdminPage() || alreadyClaimed()) return;
    fetch(`${BACKEND_URL}/api/coupon/config`)
      .then((r) => r.json())
      .then((data) => setConfig(data))
      .catch(() => {});
  }, []);

  const canShow = useCallback(() => {
    if (isAdminPage()) return false;
    if (!isAllowedPage()) return false;
    if (alreadyClaimed() || sent) return false;
    if (dismissedThisSession()) return false;
    if (!config) return false;
    if (!config.enabled) return false;
    if (config.is_sold_out) return false;
    const now = new Date();
    if (config.start_date && new Date(config.start_date) > now) return false;
    if (config.end_date && new Date(config.end_date) < now) return false;
    return true;
  }, [sent, config]);

  const show = useCallback(() => {
    if (!canShow()) return;
    setVisible(true);
  }, [canShow]);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem(SS_KEY, "true");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/coupon/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      await res.json();
      localStorage.setItem(LS_KEY, "true");
      setSent(true);
      setTimeout(() => setVisible(false), 4000);
    } catch {
      localStorage.setItem(LS_KEY, "true");
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  // Timer trigger — 150s after page load, every new session (if not already claimed or dismissed)
  useEffect(() => {
    if (!config || isAdminPage() || alreadyClaimed() || dismissedThisSession()) return;
    if (!canShow()) return;
    const timer = setTimeout(() => show(), 150000);
    return () => clearTimeout(timer);
  }, [config, show, canShow]);

  // Exit intent (desktop: mouse leaves document top) — once per session, only on allowed pages
  useEffect(() => {
    if (!config) return;
    const handleMouseLeave = (e) => {
      if (e.clientY > 0) return;
      if (visibleRef.current) return;
      if (sentRef.current) return;
      if (localStorage.getItem(LS_KEY) === "true") return;
      if (sessionStorage.getItem(SS_KEY) === "true") return;
      const path = window.location.pathname;
      if (path.startsWith("/dariousdmt") || path.startsWith("/admin")) return;
      if (!ALLOWED_PATHS.includes(path)) return;
      if (!config.enabled || config.is_sold_out) return;
      const now = new Date();
      if (config.start_date && new Date(config.start_date) > now) return;
      if (config.end_date && new Date(config.end_date) < now) return;
      setVisible(true);
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [config]);

  if (!visible || !config) return null;

  const title = lang === "es" ? (config.message_es || config.message_en) : config.message_en;
  const desc = lang === "es"
    ? (config.desc_es || "Ingresa tu email para recibir un codigo de descuento exclusivo.")
    : (config.desc_en || "Enter your email to receive an exclusive discount code.");
  const couponCode = config.code || "PVXEL10";
  const discountPct = config.discount_percent || 10;

  return (
    <div className="coupon-overlay" data-testid="coupon-popup-overlay" onClick={dismiss}>
      <div className="coupon-modal" onClick={(e) => e.stopPropagation()} data-testid="coupon-popup">
        <button onClick={dismiss} className="coupon-close" data-testid="coupon-close-btn" aria-label="Close">
          <X size={20} />
        </button>

        {sent ? (
          <div className="text-center py-6" data-testid="coupon-success">
            <div className="w-16 h-16 bg-[#14B8A6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-[#14B8A6]" />
            </div>
            <p className="font-heading font-bold text-lg text-[#0F172A] mb-2">
              {t("coupon.emailSent", lang)}
            </p>
            <p className="text-sm text-[#64748B]">{couponCode}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#F59E0B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Tag size={28} className="text-[#F59E0B]" />
              </div>
              <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-2" data-testid="coupon-title">
                {title || `${discountPct}% ${lang === "en" ? "OFF your first purchase block(s)" : "DE DESCUENTO en tu primera compra de bloque(s)"}`}
              </h2>
              <p className="text-sm text-[#64748B]">{desc}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="flex items-center gap-2 px-3 py-2 border-2 border-[#E2E8F0] focus-within:border-[#14B8A6] transition-colors">
                <Mail size={16} className="text-[#94A3B8] flex-shrink-0" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("blog.emailPlaceholder", lang)}
                  className="flex-1 outline-none text-sm bg-transparent"
                  data-testid="coupon-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full py-3 bg-[#F59E0B] text-[#0F172A] font-heading font-bold text-sm hover:bg-[#D97706] transition-colors disabled:opacity-50"
                data-testid="coupon-get-code-btn"
              >
                {sending ? t("coupon.sending", lang) : t("coupon.getCode", lang)}
              </button>
            </form>

            <button
              onClick={dismiss}
              className="block w-full text-center text-xs text-[#94A3B8] mt-3 hover:text-[#64748B] transition-colors"
              data-testid="coupon-no-thanks"
            >
              {t("coupon.noThanks", lang)}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CouponPopup;
